import { useState } from 'react';
import Modal from '../../components/common/Modal';
import ComboBox from '../../components/common/ComboBox';
import { extrairMensagemErro } from '../../components/common/ErroCarregamento';
import { AUTH_TIPO_OPCOES, METODOS } from '../../utils/webhookOpcoes';

const VAZIO = {
  nome: '',
  slug: '',
  descricao: '',
  cliente: '',
  sistema: '',
  ativo: true,
  metodos_permitidos: '*',
  auth_tipo: 'nenhum',
  auth_header: 'X-Webhook-Token',
  token: '',
  usuario_basic: '',
  senha_basic: '',
  hmac_secret: '',
  hmac_header: 'X-Hub-Signature-256',
  hmac_algoritmo: 'sha256',
  hmac_prefixo: 'sha256=',
  rejeitar_invalido: false,
  resposta_status: 200,
  resposta_corpo: '{"ok": true}',
  resposta_content_type: 'application/json',
  encaminhar_ativo: false,
  encaminhar_url: '',
  encaminhar_tentativas: 3,
  retencao_dias: 90,
};

export default function EndpointForm({ endpoint, clientes = [], sistemas = [], onClose, onSalvar }) {
  const [form, setForm] = useState(() => ({ ...VAZIO, ...(endpoint || {}) }));
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [metodosLivres, setMetodosLivres] = useState((endpoint?.metodos_permitidos || '*') === '*');

  const alterar = (campo, valor) => setForm((atual) => ({ ...atual, [campo]: valor }));

  const alternarMetodo = (metodo) => {
    const atuais = form.metodos_permitidos.split(',').map((m) => m.trim()).filter(Boolean);
    const novos = atuais.includes(metodo) ? atuais.filter((m) => m !== metodo) : [...atuais, metodo];
    alterar('metodos_permitidos', novos.join(',') || 'POST');
  };

  const enviar = async (e) => {
    e.preventDefault();
    setErro('');
    setSalvando(true);
    try {
      await onSalvar({
        ...form,
        // campos numéricos chegam como string do <input type="number">
        resposta_status: Number(form.resposta_status),
        encaminhar_tentativas: Number(form.encaminhar_tentativas),
        retencao_dias: Number(form.retencao_dias),
        metodos_permitidos: metodosLivres ? '*' : form.metodos_permitidos,
        // string vazia em campo opcional vira null para não gravar "" no banco
        slug: form.slug || null,
        descricao: form.descricao || null,
        // nomes, não ids: o backend reusa o cadastro existente ou cria o primeiro
        cliente: form.cliente || null,
        sistema: form.sistema || null,
        encaminhar_url: form.encaminhar_url || null,
        token: form.token || null,
        usuario_basic: form.usuario_basic || null,
        senha_basic: form.senha_basic || null,
        hmac_secret: form.hmac_secret || null,
      });
      onClose();
    } catch (err) {
      setErro(extrairMensagemErro(err, 'Não foi possível salvar o endpoint.'));
    } finally {
      setSalvando(false);
    }
  };

  const descricaoAuth = AUTH_TIPO_OPCOES.find((t) => t.value === form.auth_tipo)?.descricao;

  return (
    <Modal titulo={endpoint ? `Editar ${endpoint.nome}` : 'Novo endpoint'} onClose={onClose} largura={640}>
      <form onSubmit={enviar}>
        <fieldset className="wh-fieldset">
          <legend>Identificação</legend>
          <div className="row g-2">
            <div className="col-12 col-md-6">
              <label className="wh-label">Nome *</label>
              <input
                className="wh-input"
                value={form.nome}
                onChange={(e) => alterar('nome', e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="wh-label">Slug (URL)</label>
              <input
                className="wh-input"
                value={form.slug || ''}
                onChange={(e) => alterar('slug', e.target.value)}
                placeholder="derivado do nome"
              />
              <div className="wh-hint">A URL pública fica /hooks/&lt;slug&gt;. Mudar o slug quebra a configuração já feita no provedor.</div>
            </div>
            <div className="col-12 col-md-6">
              <label className="wh-label" htmlFor="campo-cliente">Cliente</label>
              <ComboBox
                id="campo-cliente"
                valor={form.cliente}
                opcoes={clientes}
                onChange={(v) => alterar('cliente', v)}
                placeholder="escolha ou digite um novo"
              />
              <div className="wh-hint">De quem é a integração. O mesmo sistema costuma atender vários clientes.</div>
            </div>
            <div className="col-12 col-md-6">
              <label className="wh-label" htmlFor="campo-sistema">Sistema</label>
              <ComboBox
                id="campo-sistema"
                valor={form.sistema}
                opcoes={sistemas}
                onChange={(v) => alterar('sistema', v)}
                placeholder="Tiny, WooCommerce, Asaas..."
              />
              <div className="wh-hint">Com o que ele fala. A detecção automática por cabeçalho/payload continua valendo.</div>
            </div>
            <div className="col-12 col-md-6">
              <label className="wh-label">Situação</label>
              <select className="wh-select" value={form.ativo ? '1' : '0'} onChange={(e) => alterar('ativo', e.target.value === '1')}>
                <option value="1">Ativo — recebe e guarda</option>
                <option value="0">Inativo — responde 410 e não guarda</option>
              </select>
            </div>
            <div className="col-12">
              <label className="wh-label">Descrição</label>
              <textarea
                className="wh-textarea"
                rows={2}
                value={form.descricao || ''}
                onChange={(e) => alterar('descricao', e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="wh-fieldset">
          <legend>Métodos aceitos</legend>
          <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 13 }}>
            <input type="checkbox" checked={metodosLivres} onChange={(e) => setMetodosLivres(e.target.checked)} />
            Aceitar qualquer método (recomendado para capturar tudo)
          </label>
          {!metodosLivres && (
            <div className="d-flex gap-2 flex-wrap">
              {METODOS.map((m) => (
                <label key={m} className="d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={form.metodos_permitidos.split(',').map((x) => x.trim()).includes(m)}
                    onChange={() => alternarMetodo(m)}
                  />
                  {m}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <fieldset className="wh-fieldset">
          <legend>Autenticação da chamada</legend>
          <select className="wh-select" value={form.auth_tipo} onChange={(e) => alterar('auth_tipo', e.target.value)}>
            {AUTH_TIPO_OPCOES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          <div className="wh-hint mb-2">{descricaoAuth}</div>

          {form.auth_tipo === 'token' && (
            <div className="row g-2">
              <div className="col-12 col-md-5">
                <label className="wh-label">Cabeçalho</label>
                <input className="wh-input" value={form.auth_header} onChange={(e) => alterar('auth_header', e.target.value)} />
              </div>
              <div className="col-12 col-md-7">
                <label className="wh-label">Token esperado</label>
                <input
                  className="wh-input wh-mono"
                  value={form.token || ''}
                  onChange={(e) => alterar('token', e.target.value)}
                  placeholder="deixe vazio para gerar automaticamente"
                />
              </div>
            </div>
          )}

          {form.auth_tipo === 'basic' && (
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <label className="wh-label">Usuário</label>
                <input className="wh-input" value={form.usuario_basic || ''} onChange={(e) => alterar('usuario_basic', e.target.value)} />
              </div>
              <div className="col-12 col-md-6">
                <label className="wh-label">Senha</label>
                <input className="wh-input" value={form.senha_basic || ''} onChange={(e) => alterar('senha_basic', e.target.value)} />
              </div>
            </div>
          )}

          {form.auth_tipo === 'hmac' && (
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <label className="wh-label">Segredo compartilhado</label>
                <input
                  className="wh-input wh-mono"
                  value={form.hmac_secret || ''}
                  onChange={(e) => alterar('hmac_secret', e.target.value)}
                  placeholder="deixe vazio para gerar automaticamente"
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="wh-label">Cabeçalho da assinatura</label>
                <input className="wh-input" value={form.hmac_header} onChange={(e) => alterar('hmac_header', e.target.value)} />
              </div>
              <div className="col-6 col-md-3">
                <label className="wh-label">Algoritmo</label>
                <select className="wh-select" value={form.hmac_algoritmo} onChange={(e) => alterar('hmac_algoritmo', e.target.value)}>
                  <option value="sha256">sha256</option>
                  <option value="sha1">sha1</option>
                  <option value="sha512">sha512</option>
                </select>
              </div>
              <div className="col-6 col-md-3">
                <label className="wh-label">Prefixo</label>
                <input className="wh-input" value={form.hmac_prefixo} onChange={(e) => alterar('hmac_prefixo', e.target.value)} />
              </div>
              <div className="col-12">
                <div className="wh-hint">
                  Aceitamos a assinatura em hex, em base64 e no formato com vírgulas (t=...,v1=...) — não é preciso saber
                  qual o provedor usa.
                </div>
              </div>
            </div>
          )}

          {form.auth_tipo !== 'nenhum' && (
            <label className="d-flex align-items-center gap-2 mt-2" style={{ fontSize: 13 }}>
              <input
                type="checkbox"
                checked={form.rejeitar_invalido}
                onChange={(e) => alterar('rejeitar_invalido', e.target.checked)}
              />
              Responder 401 quando a credencial não bater
              <span className="wh-hint">(o evento continua sendo gravado, marcado como rejeitado)</span>
            </label>
          )}
        </fieldset>

        <fieldset className="wh-fieldset">
          <legend>Resposta devolvida ao provedor</legend>
          <div className="row g-2">
            <div className="col-6 col-md-3">
              <label className="wh-label">Status HTTP</label>
              <input
                type="number"
                className="wh-input"
                min={200}
                max={599}
                value={form.resposta_status}
                onChange={(e) => alterar('resposta_status', e.target.value)}
              />
            </div>
            <div className="col-6 col-md-4">
              <label className="wh-label">Content-Type</label>
              <input
                className="wh-input"
                value={form.resposta_content_type}
                onChange={(e) => alterar('resposta_content_type', e.target.value)}
              />
            </div>
            <div className="col-12 col-md-5">
              <label className="wh-label">Corpo</label>
              <input
                className="wh-input wh-mono"
                value={form.resposta_corpo}
                onChange={(e) => alterar('resposta_corpo', e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="wh-fieldset">
          <legend>Encaminhamento e retenção</legend>
          <label className="d-flex align-items-center gap-2 mb-2" style={{ fontSize: 13 }}>
            <input
              type="checkbox"
              checked={form.encaminhar_ativo}
              onChange={(e) => alterar('encaminhar_ativo', e.target.checked)}
            />
            Encaminhar cada evento recebido para outra URL
          </label>
          {form.encaminhar_ativo && (
            <div className="row g-2 mb-2">
              <div className="col-12 col-md-9">
                <label className="wh-label">URL de destino</label>
                <input
                  className="wh-input"
                  value={form.encaminhar_url || ''}
                  onChange={(e) => alterar('encaminhar_url', e.target.value)}
                  placeholder="https://sistema-interno/endpoint"
                />
              </div>
              <div className="col-12 col-md-3">
                <label className="wh-label">Tentativas</label>
                <input
                  type="number"
                  className="wh-input"
                  min={1}
                  max={10}
                  value={form.encaminhar_tentativas}
                  onChange={(e) => alterar('encaminhar_tentativas', e.target.value)}
                />
              </div>
            </div>
          )}
          <div style={{ maxWidth: 220 }}>
            <label className="wh-label">Retenção (dias)</label>
            <input
              type="number"
              className="wh-input"
              min={0}
              max={3650}
              value={form.retencao_dias}
              onChange={(e) => alterar('retencao_dias', e.target.value)}
            />
            <div className="wh-hint">0 = nunca expurgar. O expurgo roda quando um admin manda rodar.</div>
          </div>
        </fieldset>

        {erro && (
          <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>
            {erro}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="wh-btn wh-btn-outline" onClick={onClose} disabled={salvando}>
            Cancelar
          </button>
          <button type="submit" className="wh-btn wh-btn-primary" disabled={salvando}>
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
